import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import {
  CATALOG_SORTS, SEARCH_SORTS, CATALOG_TYPES, CATALOG_KINDS, CATALOG_SHOW, RARITY_BUCKETS,
  TYPE_COLORS, EMPTY_CATALOG_FILTERS, EMPTY_COLLECTION_FILTERS, shortEraLabel, filterList,
  toggleFilter, countCatalogChoices, sortRows,
} from '../../services/cardData/catalogFilters';

const PANES = [{ id: 'sort', label: 'Sort' }, { id: 'filter', label: 'Filter' }];

/**
 * One labelled row of multi-select chips.
 *
 * Selection is shown by fill, not by a tick: at five or six ticks in a group a
 * row of checkmarks is harder to read at a glance than a row of filled chips,
 * and the fill is what a child scans for.
 */
/**
 * One labelled row of multi-select chips.
 *
 * Selection is shown by fill, not by a tick: at five or six ticks in a group a
 * row of checkmarks is harder to read at a glance than a row of filled chips,
 * and the fill is what a child scans for.
 *
 * `available` is the set of ids the person actually owns, and it is optional -
 * the catalogue passes nothing, because every option exists there. When it is
 * given, an option outside it renders faded AND is genuinely unusable:
 * `disabled` so it cannot be tapped, `tabIndex={-1}` and `aria-disabled` so it
 * cannot be reached by keyboard or read as actionable. Dimming alone would be a
 * button that looks dead and still works, which is worse than either.
 *
 * A chip that is faded but TICKED stays usable. Someone filters to Fairy and
 * then trades away their last Fairy card: the filter must not silently drop
 * itself and show everything, and they must be able to switch it off. So an
 * unavailable-but-ticked chip keeps its fill, gains a strike, and remains
 * tappable - it is the only way out of the empty grid it is causing.
 */
const ChipGroup = ({ label, theme, options, selected, onToggle, available = null }) => (
  <div style={styles.section}>
    <label style={{ ...styles.label, color: theme.textSecondary }}>{label}</label>
    <div style={styles.chipWrap}>
      {options.map((option) => {
        const on = selected.includes(option.id);
        const owned = !available || available.has(option.id);
        const tint = option.tint || theme.accent;
        const dead = !owned && !on;
        return (
          <button
            key={option.id}
            disabled={dead}
            tabIndex={dead ? -1 : 0}
            aria-disabled={dead}
            style={{
              ...styles.catalogChip,
              backgroundColor: on ? tint : theme.bgCard,
              borderColor: on ? tint : theme.border,
              color: on ? (option.tint ? '#10151b' : '#fff') : theme.text,
              opacity: owned ? 1 : 0.38,
              cursor: dead ? 'default' : 'pointer',
              textDecoration: !owned && on ? 'line-through' : 'none',
            }}
            onClick={() => { if (!dead) onToggle(option.id); }}
          >
            {option.tint && (
              <span style={{ ...styles.typeDot, backgroundColor: on ? '#10151b' : tint }} />
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ResetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SortFilterModal = ({
  isOpen,
  onClose,
  mode, // 'cards' | 'binders' | 'binder-detail' | 'wishlist-sets' | 'wishlist-cards' | 'cards-sets' | 'set-cards' | 'catalog' | 'search'
  isOwnCollection,
  // Filter state
  tradeFilter, // 'all' | 'pc' | 'tradable'
  setTradeFilter,
  visibilityFilter, // 'all' | 'public' | 'private'
  setVisibilityFilter,
  imageFilter, // 'digital' | 'photo'
  setImageFilter,
  // Sort state
  sortBy,
  setSortBy,
  // Sets filter (for cards-sets mode)
  setsFilter, // 'all' | 'owned'
  setSetsFilter,
  // Sets language (for cards-sets mode)
  setsLanguage, // 'en' | 'ja' | 'zh-tw' | etc.
  setSetsLanguage,
  // Catalogue filters (for 'catalog' mode) — { show, kind, type, rarity, era }
  catalogFilters,
  setCatalogFilters,
  // Collection filters (All tab and binder detail) — { types, rarities, eras }
  cardFilters,
  setCardFilters,
  // What the collection actually holds, for fading what it does not.
  collectionFacets,
  // What the CURRENT RESULT SET holds, for the same reason, on the search
  // sheet. The whole catalogue passes nothing, because every option exists
  // there; a search for "magikarp" passes the rarities, types, kinds and eras
  // those Magikarps actually have. Shape matches collectionFacets plus a
  // `capped` flag, and `null` OR `capped` both mean fade nothing - "I could not
  // count this" must never look like "there are none of these".
  catalogFacets = null,
  // Eras for the catalogue's era row: [{ id, display_name }]
  catalogEras,
  // Which sorts the database can actually answer right now.
  catalogPriceSortsReady = true,
  // Reset handler
  onReset,
}) => {
  const { theme } = useTheme();
  const [languageExpanded, setLanguageExpanded] = useState(false);
  // Which pane of the catalogue sheet is showing. Starts on Sort, because sort
  // is the control that does the most and the one most often reached for.
  const [pane, setPane] = useState('sort');
  // What the Filter tab's badge counts. The catalogue counts ticked chips; the
  // collection screens count the segmented controls that are off their default,
  // so the badge means the same thing in both places: how many things are
  // narrowing what you see.
  // `null` facets means the read has not answered yet, which is NOT the same as
  // owning nothing: passing null leaves every chip usable, so a slow network
  // never greys out a collection that is really there.
  const ownedTypes = collectionFacets ? new Set(collectionFacets.types) : null;
  const ownedRarities = collectionFacets ? new Set(collectionFacets.rarities) : null;
  const ownedEras = collectionFacets ? new Set(collectionFacets.eras) : null;

  // Same rule, one level out: a result set too large to have been counted
  // honestly fades nothing rather than guessing.
  const resultFacets = catalogFacets && !catalogFacets.capped ? catalogFacets : null;
  const resultTypes = resultFacets ? new Set(resultFacets.types) : null;
  const resultRarities = resultFacets ? new Set(resultFacets.rarities) : null;
  const resultEras = resultFacets ? new Set(resultFacets.eras) : null;
  const resultKinds = resultFacets ? new Set(resultFacets.kinds) : null;
  const resultShow = resultFacets ? new Set(resultFacets.show) : null;

  const collectionChoiceCount = (tradeFilter && tradeFilter !== 'all' ? 1 : 0)
    + (visibilityFilter && visibilityFilter !== 'all' ? 1 : 0)
    + countCatalogChoices(cardFilters);
  const choiceCount = mode === 'catalog' || mode === 'search'
    ? countCatalogChoices(catalogFilters)
    : collectionChoiceCount;

  if (!isOpen) return null;

  const isBinderMode = mode === 'binders' || mode === 'binder-detail';
  const isWishListMode = mode === 'wishlist-sets' || mode === 'wishlist-cards';
  const isCardsSetsMode = mode === 'cards-sets';
  const isSetCardsMode = mode === 'set-cards'; // Cards within a selected set
  const isCatalogMode = mode === 'catalog';    // The whole 23,591-card catalogue
  // Search results. A place of its own, with its own remembered sort and its
  // own filters, because a sort chosen while looking for Magikarps must not
  // become the order of the whole catalogue next time it is opened.
  const isSearchMode = mode === 'search';
  // Which modes get the Sort | Filter split. The owner wanted the All tab
  // (mode 'cards') to stop having "a very different UI" from the catalogue, so
  // it gets the same two panes, carrying its own collection-only filters rather
  // than the catalogue's.
  const isPaned = isCatalogMode || isSearchMode || mode === 'cards' || isBinderMode;

  const tradeOptions = [
    { id: 'all', label: 'All' },
    { id: 'pc', label: 'PC' },
    { id: 'tradable', label: 'Tradable' },
  ];

  const visibilityOptions = [
    { id: 'all', label: 'All' },
    { id: 'public', label: 'Public' },
    { id: 'private', label: 'Private' },
  ];

  const imageOptions = [
    { id: 'digital', label: 'Digital' },
    { id: 'photo', label: 'Photo' },
  ];

  const setsFilterOptions = [
    { id: 'all', label: 'All Sets' },
    { id: 'owned', label: 'Owned Only' },
  ];

  const languageOptions = [
    { id: 'en', label: 'English' },
    { id: 'ja', label: 'Japanese' },
    { id: 'zh-tw', label: 'Chinese (Traditional)' },
    { id: 'es', label: 'Spanish' },
    { id: 'fr', label: 'French' },
    { id: 'it', label: 'Italian' },
    { id: 'de', label: 'German' },
    { id: 'pt', label: 'Portuguese' },
  ];

  const getCurrentLanguageLabel = () => {
    const lang = languageOptions.find(l => l.id === setsLanguage);
    return lang?.label || 'English';
  };

  const cardSortOptions = [
    { id: 'recent', label: 'Recently Added' },
    { id: 'value-desc', label: 'Value High-Low', pair: 'value' },
    { id: 'value-asc', label: 'Value Low-High', pair: 'value' },
    { id: 'name-asc', label: 'Name A-Z', pair: 'name' },
    { id: 'name-desc', label: 'Name Z-A', pair: 'name' },
    { id: 'set-newest', label: 'Newest Set', pair: 'set' },
    { id: 'set-oldest', label: 'Oldest Set', pair: 'set' },
    // The same era and rarity orders the catalogue offers. One vocabulary
    // across all three screens, so learning the sheet once is enough.
    { id: 'era-newest', label: 'Newest Era', pair: 'era' },
    { id: 'era-oldest', label: 'Oldest Era', pair: 'era' },
    { id: 'rarity-desc', label: 'Rarity High-Low', pair: 'rarity' },
    { id: 'rarity-asc', label: 'Rarity Low-High', pair: 'rarity' },
    { id: 'hp-desc', label: 'HP High-Low', pair: 'hp' },
    { id: 'hp-asc', label: 'HP Low-High', pair: 'hp' },
    { id: 'type', label: 'Type' },
  ];
  // Older saved preference id for the same sort.
  const currentSort = sortBy === 'hp' ? 'hp-desc' : sortBy;

  const binderSortOptions = [
    { id: 'newest', label: 'Newest', pair: 'age' },
    { id: 'oldest', label: 'Oldest', pair: 'age' },
    { id: 'name-asc', label: 'A-Z', pair: 'name' },
    { id: 'name-desc', label: 'Z-A', pair: 'name' },
    { id: 'value-desc', label: 'Value High-Low', pair: 'value' },
    { id: 'value-asc', label: 'Value Low-High', pair: 'value' },
  ];

  const setsSortOptions = [
    { id: 'newest', label: 'Newest First', pair: 'age' },
    { id: 'oldest', label: 'Oldest First', pair: 'age' },
    { id: 'a-z', label: 'A-Z', pair: 'name' },
    { id: 'z-a', label: 'Z-A', pair: 'name' },
  ];

  const wishListSetsSortOptions = [
    { id: 'newest', label: 'Newest First', pair: 'age' },
    { id: 'oldest', label: 'Oldest First', pair: 'age' },
    { id: 'name-asc', label: 'A-Z', pair: 'name' },
    { id: 'name-desc', label: 'Z-A', pair: 'name' },
  ];

  const wishListCardsSortOptions = [
    { id: 'recent', label: 'Recently Added' },
    { id: 'name-asc', label: 'Name A-Z', pair: 'name' },
    { id: 'name-desc', label: 'Name Z-A', pair: 'name' },
  ];

  // Sort options for cards within a set
  const setCardsSortOptions = [
    { id: 'number-desc', label: 'Card # High-Low', pair: 'number' },
    { id: 'number-asc', label: 'Card # Low-High', pair: 'number' },
    { id: 'name-asc', label: 'Name A-Z', pair: 'name' },
    { id: 'name-desc', label: 'Name Z-A', pair: 'name' },
  ];

  // The catalogue's orders are the collection's orders, minus the two it
  // cannot have: "Recently Added" (a catalogue card was never added by anyone)
  // and "Type" (which is a filter here, not an order). Same ids, same wording,
  // so the two screens read as one app.
  const catalogSortOptions = CATALOG_SORTS
    .filter((o) => (o.needsPrice ? catalogPriceSortsReady : true));

  // The search's orders are the catalogue's, plus Best Match and minus HP. Same
  // ids, same labels, one vocabulary - see SEARCH_SORTS for why.
  const searchSortOptions = SEARCH_SORTS
    .filter((o) => (o.needsPrice ? catalogPriceSortsReady : true));

  const sortOptions = isSearchMode
    ? searchSortOptions
    : isCatalogMode
    ? catalogSortOptions
    : isSetCardsMode
    ? setCardsSortOptions
    : isBinderMode
    ? binderSortOptions
    : isCardsSetsMode
      ? setsSortOptions
      : mode === 'wishlist-sets'
        ? wishListSetsSortOptions
        : mode === 'wishlist-cards'
          ? wishListCardsSortOptions
          : cardSortOptions;

  const getTitle = () => {
    if (mode === 'binder-detail') return 'Sort & Filter Binder';
    if (isBinderMode) return 'Sort Binders';
    if (isWishListMode) return 'Sort Wish List';
    if (isCardsSetsMode) return 'Sort & Filter Sets';
    if (isSetCardsMode) return 'Sort Cards';
    if (isSearchMode) return 'Sort & Filter Results';
    if (isCatalogMode) return 'Sort & Filter Cards';
    return 'Filter & Sort';
  };

  // Rendered on document.body, not where it is called from.
  //
  // This sheet is opened from inside the page scroller, and a z-index only
  // outranks siblings within its own stacking context. The Collection header
  // and its All / Binder / Explore / Wish pill are outside that context and
  // paint later, so a sheet at z-index 1000 was appearing UNDER the tabs: the
  // owner tapped Filter and read "Sort" and "Explore" drawn on top of one
  // another. Portalling lifts it to the document's own stacking context, where
  // 1000 means what it looks like it means. Same reason WishPickerAddSheet
  // portals, and the same iOS WebKit trap that one documents.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{...styles.backdrop, backgroundColor: theme.overlay}}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{...styles.modal, backgroundColor: theme.bgSheet}}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {/* Header */}
            <div style={styles.header}>
              <h2 style={{...styles.title, color: theme.text}}>
                {getTitle()}
              </h2>
              <div style={styles.headerButtons}>
                {onReset && (
                  <button
                    style={{...styles.resetButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary}}
                    onClick={() => {
                      onReset();
                      onClose();
                    }}
                  >
                    <ResetIcon />
                    <span>Reset</span>
                  </button>
                )}
                <button style={{...styles.closeButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary}} onClick={onClose}>
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* Language Selector - Only for cards-sets mode */}
            {isCardsSetsMode && setSetsLanguage && (
              <>
                <div style={styles.section}>
                  <button
                    style={{
                      ...styles.languageSelector,
                      backgroundColor: theme.bgCard,
                      borderColor: languageExpanded ? theme.accent : theme.border,
                    }}
                    onClick={() => setLanguageExpanded(!languageExpanded)}
                  >
                    <span style={{ color: theme.text, fontWeight: '500' }}>
                      {getCurrentLanguageLabel()}
                    </span>
                    <ChevronIcon expanded={languageExpanded} />
                  </button>

                  <AnimatePresence>
                    {languageExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={styles.languageList}>
                          {languageOptions.map((option) => (
                            <button
                              key={option.id}
                              style={{
                                ...styles.languageOption,
                                backgroundColor: setsLanguage === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                                borderColor: setsLanguage === option.id ? theme.accent : theme.border,
                                color: setsLanguage === option.id ? theme.accent : theme.text,
                              }}
                              onClick={() => {
                                setSetsLanguage(option.id);
                                setLanguageExpanded(false);
                              }}
                            >
                              {option.label}
                              {setsLanguage === option.id && (
                                <span style={{ color: theme.accent }}><CheckIcon /></span>
                              )}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* Sets Filter Section - Only for cards-sets mode */}
            {isCardsSetsMode && setSetsFilter && (
              <>
                <div style={styles.section}>
                  <label style={{...styles.label, color: theme.textSecondary}}>Show</label>
                  <div style={styles.chipRow}>
                    {setsFilterOptions.map((option) => (
                      <button
                        key={option.id}
                        style={{
                          ...styles.chip,
                          backgroundColor: setsFilter === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                          borderColor: setsFilter === option.id ? theme.accent : theme.border,
                          color: setsFilter === option.id ? theme.accent : theme.textMuted,
                        }}
                        onClick={() => setSetsFilter(option.id)}
                      >
                        {option.label}
                        {setsFilter === option.id && (
                          <span style={styles.chipCheck}><CheckIcon /></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* Filters, one quiet line per group. The sort list is the point of
                this sheet; these should not crowd it. */}
            {/* Sort | Filter, two panes.

                The owner's structure, verbatim: "what if the top there's a
                toggle: Sort | Filter and this way you have the sorting options
                on one clean space, and filter on another". They do different
                jobs - sort decides the order AND the separators, filter only
                includes and excludes - and mixing them in one scroll was what
                buried the orders under five rows of era chips. */}
            {isPaned && (
              <div style={{ ...styles.paneToggle, backgroundColor: theme.bgCard, borderColor: theme.border }}>
                {PANES.map((p) => (
                  <button
                    key={p.id}
                    style={{
                      ...styles.paneBtn,
                      backgroundColor: pane === p.id ? theme.accent : 'transparent',
                      color: pane === p.id ? '#fff' : theme.textSecondary,
                    }}
                    onClick={() => setPane(p.id)}
                  >
                    {p.label}
                    {p.id === 'filter' && choiceCount > 0 && (
                      <span style={{
                        ...styles.paneCount,
                        backgroundColor: pane === p.id ? 'rgba(0, 0, 0, 0.28)' : theme.accent,
                      }}>
                        {choiceCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {pane === 'filter' && !isWishListMode && !isCardsSetsMode && !isSetCardsMode && !isCatalogMode && !isSearchMode && (
              <>
                <div style={styles.filterRows}>
                  <div style={styles.filterRow}>
                    <span style={{ ...styles.filterName, color: theme.textMuted }}>Show</span>
                    <div style={{ ...styles.segment, backgroundColor: theme.bgCard, borderColor: theme.border }}>
                      {tradeOptions.map((option) => (
                        <button
                          key={option.id}
                          style={{
                            ...styles.segmentBtn,
                            backgroundColor: tradeFilter === option.id ? theme.accent : 'transparent',
                            color: tradeFilter === option.id ? '#fff' : theme.textSecondary,
                          }}
                          onClick={() => setTradeFilter(option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isOwnCollection && (
                    <div style={styles.filterRow}>
                      <span style={{ ...styles.filterName, color: theme.textMuted }}>Visibility</span>
                      <div style={{ ...styles.segment, backgroundColor: theme.bgCard, borderColor: theme.border }}>
                        {visibilityOptions.map((option) => (
                          <button
                            key={option.id}
                            style={{
                              ...styles.segmentBtn,
                              backgroundColor: visibilityFilter === option.id ? theme.accent : 'transparent',
                              color: visibilityFilter === option.id ? '#fff' : theme.textSecondary,
                            }}
                            onClick={() => setVisibilityFilter(option.id)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={styles.filterRow}>
                    <span style={{ ...styles.filterName, color: theme.textMuted }}>Image</span>
                    <div style={{ ...styles.segment, backgroundColor: theme.bgCard, borderColor: theme.border }}>
                      {imageOptions.map((option) => (
                        <button
                          key={option.id}
                          style={{
                            ...styles.segmentBtn,
                            backgroundColor: imageFilter === option.id ? theme.accent : 'transparent',
                            color: imageFilter === option.id ? '#fff' : theme.textSecondary,
                          }}
                          onClick={() => setImageFilter(option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Type, rarity and era, the same three the catalogue offers.
                    Faded where the collection holds none, so the shape of what
                    you are missing is visible rather than hidden. */}
                {cardFilters && setCardFilters && (
                  <>
                    <ChipGroup
                      label="Type" theme={theme}
                      options={CATALOG_TYPES.map((t) => ({ id: t, label: t, tint: TYPE_COLORS[t] }))}
                      selected={filterList(cardFilters, 'types')}
                      available={ownedTypes}
                      onToggle={(v) => setCardFilters(toggleFilter(cardFilters, 'types', v))}
                    />
                    <ChipGroup
                      label="Rarity" theme={theme}
                      options={RARITY_BUCKETS.map((b) => ({ id: b.id, label: b.label }))}
                      selected={filterList(cardFilters, 'rarities')}
                      available={ownedRarities}
                      onToggle={(v) => setCardFilters(toggleFilter(cardFilters, 'rarities', v))}
                    />
                    {Boolean(catalogEras?.length) && (
                      <ChipGroup
                        label="Era" theme={theme}
                        options={catalogEras.map((e) => ({ id: e.id, label: shortEraLabel(e) }))}
                        selected={filterList(cardFilters, 'eras')}
                        available={ownedEras}
                        onToggle={(v) => setCardFilters(toggleFilter(cardFilters, 'eras', v))}
                      />
                    )}
                    {collectionChoiceCount > 0 && (
                      <button
                        style={{ ...styles.clearAll, borderColor: theme.border, color: theme.textSecondary }}
                        onClick={() => setCardFilters(EMPTY_COLLECTION_FILTERS)}
                      >
                        Clear all filters
                      </button>
                    )}
                  </>
                )}

                <div style={{...styles.divider, backgroundColor: theme.border}} />
              </>
            )}

            {/* The Filter pane. Every group multi-select, every group an OR
                inside itself and an AND between, which is what a person means
                by ticking Grass and Lightning, then Trainer and Energy.

                No "Any" chip anywhere. Having chosen nothing in a group already
                means all of it, so an Any would be a second way to say the same
                thing and a third state to keep in sync. */}
            {(isCatalogMode || isSearchMode) && pane === 'filter' && catalogFilters && setCatalogFilters && (
              <>
                <ChipGroup
                  label="Show" theme={theme}
                  options={CATALOG_SHOW}
                  selected={filterList(catalogFilters, 'show')}
                  available={resultShow}
                  onToggle={(v) => setCatalogFilters(toggleFilter(catalogFilters, 'show', v))}
                />
                <ChipGroup
                  label="Kind" theme={theme}
                  options={CATALOG_KINDS}
                  selected={filterList(catalogFilters, 'kinds')}
                  available={resultKinds}
                  onToggle={(v) => setCatalogFilters(toggleFilter(catalogFilters, 'kinds', v))}
                />
                <ChipGroup
                  label="Type" theme={theme}
                  options={CATALOG_TYPES.map((t) => ({ id: t, label: t, tint: TYPE_COLORS[t] }))}
                  selected={filterList(catalogFilters, 'types')}
                  available={resultTypes}
                  onToggle={(v) => setCatalogFilters(toggleFilter(catalogFilters, 'types', v))}
                />
                <ChipGroup
                  label="Rarity" theme={theme}
                  options={RARITY_BUCKETS.map((b) => ({ id: b.id, label: b.label }))}
                  selected={filterList(catalogFilters, 'rarities')}
                  available={resultRarities}
                  onToggle={(v) => setCatalogFilters(toggleFilter(catalogFilters, 'rarities', v))}
                />
                {Boolean(catalogEras?.length) && (
                  <ChipGroup
                    label="Era" theme={theme}
                    options={catalogEras.map((e) => ({ id: e.id, label: shortEraLabel(e) }))}
                    selected={filterList(catalogFilters, 'eras')}
                    available={resultEras}
                    onToggle={(v) => setCatalogFilters(toggleFilter(catalogFilters, 'eras', v))}
                  />
                )}
                {choiceCount > 0 && (
                  <button
                    style={{ ...styles.clearAll, borderColor: theme.border, color: theme.textSecondary }}
                    onClick={() => setCatalogFilters(EMPTY_CATALOG_FILTERS)}
                  >
                    Clear all filters
                  </button>
                )}
              </>
            )}

            {/* Sort Section. Single choice, always - it is the one control that
                decides what order 23,591 cards come back in. */}
            {(!isPaned || pane === 'sort') && (
              <div style={styles.section}>
                <label style={{...styles.label, color: theme.textSecondary}}>Sort By</label>
                {/* Two columns, an order beside its opposite. A lone order with
                    no opposite takes the whole row rather than sitting in the
                    left column next to a hole that reads as a missing option. */}
                <div style={styles.sortGrid}>
                  {sortRows(sortOptions).map((row) => (
                    <div key={row.map((o) => o.id).join('|')} style={styles.sortRow}>
                      {row.map((option) => (
                        <button
                          key={option.id}
                          style={{
                            ...styles.sortCell,
                            backgroundColor: currentSort === option.id ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                            borderColor: currentSort === option.id ? theme.accent : theme.border,
                            color: theme.text,
                          }}
                          onClick={() => {
                            setSortBy(option.id);
                            onClose();
                          }}
                        >
                          <span style={styles.sortLabel}>{option.label}</span>
                          {currentSort === option.id && (
                            <span style={{...styles.sortCheck, color: theme.accent}}><CheckIcon /></span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const styles = {
  paneToggle: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '12px',
    marginBottom: '16px',
  },
  paneBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '9px 10px',
    borderWidth: '0',
    borderStyle: 'solid',
    borderRadius: '9px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
  },
  paneCount: {
    minWidth: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    fontSize: '10px',
    fontWeight: '700',
    lineHeight: '18px',
    color: '#fff',
    textAlign: 'center',
  },
  sortGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sortRow: {
    display: 'flex',
    gap: '8px',
  },
  sortCell: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    padding: '13px 14px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
  },
  clearAll: {
    width: '100%',
    marginTop: '4px',
    padding: '10px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '10px',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  // Deliberately NOT built on `chip`. That one sets the `border` shorthand and
  // every caller then overrides `borderColor`, which is the shorthand/longhand
  // mix the house rule forbids. Here the width and style are longhand, so an
  // override of the colour alone is complete.
  catalogChip: {
    flex: '0 0 auto',
    padding: '8px 11px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
  },
  typeDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: '80px',
    left: '8px',
    right: '8px',
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    overflowX: 'hidden',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 120px)',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  section: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  chipRow: {
    display: 'flex',
    gap: '8px',
  },
  chip: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  chipCheck: {
    display: 'flex',
    alignItems: 'center',
  },
  filterRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  filterName: {
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  segment: {
    display: 'flex',
    padding: '2px',
    borderRadius: '9px',
    border: '1px solid',
  },
  segmentBtn: {
    padding: '5px 11px',
    borderRadius: '7px',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: '0',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    transition: 'background-color 0.15s ease, color 0.15s ease',
  },
  divider: {
    height: '1px',
    margin: '16px 0',
  },
  sortList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  sortOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '11px 16px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  sortLabel: {
    fontWeight: '500',
  },
  sortCheck: {
    display: 'flex',
    alignItems: 'center',
  },
  toggleButton: {
    padding: '6px 14px',
    borderRadius: '16px',
    border: '1px solid',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'all 0.15s ease',
  },
  toggleLabel: {
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  languageSelector: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '15px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'border-color 0.15s ease',
  },
  languageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '8px',
  },
  languageOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    border: '1px solid',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  },
};

export default SortFilterModal;
